import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, TextField, Typography, MenuItem } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Transaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { useWallet } from '@solana/wallet-adapter-react';
import { Country, State } from 'country-state-city';
import { stripePromise } from '../../../utils/stripe';
import { useNetwork } from '../../../utils/network';
import { useUser } from '../../../contexts/UserContext';
import { useWalletReady } from '../../../utils/useWalletReady';
import { PT, fieldSx, primaryBtnSx, ghostBtnSx, panelSx, kickerSx, dialogPaperSx } from '../../../components/portalUi';
import { selectMenuProps, selectSx } from '../../../components/portalSelect';
import { PaymentForm, type Shipping } from '../PhysicalCards/PhysicalCards';
import AuctionCountdown from '../../Auction/AuctionCountdown';

/* THE INCRYPT BREAK (the card auction) — the live pack opening of the physical Angelic set (server:
 * lib/cardAuction.js; the rulebook docs/angelic-card-auction.md).
 *
 * Every card of the set is a lot. A member bids INC on a card and PAYS when they bid —
 * earned INC in one call, or wallet INC on-chain (intent → sign → confirm, the tournament
 * fee's steps). Outbid, they get the whole amount straight back as earned INC. At the close
 * the leading bid on each card is final; on stream the admin ticks the cards as they are
 * pulled and this page shows them; after the opening a winner enters an address, sees the
 * postage for one parcel and pays it here. The rules are spelled out in HOW IT WORKS, and
 * the one that matters — a secured card's bid stands whether or not it is drawn — is said
 * in the bid sheet before the first bid. */

// the portal's sx tokens are SxProps unions; spread as plain objects
const ghostSx = ghostBtnSx as Record<string, unknown>;
const panelPlain = panelSx as Record<string, unknown>;
const primaryPlain = primaryBtnSx as Record<string, unknown>;
const GOLD = '#e8c877';
const GOLD_SOFT = 'rgba(232,200,119,0.14)';
const GOLD_BORDER = 'rgba(232,200,119,0.5)';
const RED = '#ff8a8a';
const GREEN = '#5fe3a1';
const INK = '#eef6fb';
const MUTED = '#8ea3b3';
const MONO = "ui-monospace, Menlo, Consolas, monospace";
const mono = `800 10px ${MONO}`;
const stencil = { font: mono, letterSpacing: '0.22em', textTransform: 'uppercase' as const, color: MUTED };
const goldBtn = { ...primaryPlain, background: GOLD, color: '#0b0f14', boxShadow: `0 10px 30px -12px ${GOLD}`, '&:hover': { background: '#f0d58c' } };
const RARITY_HEX: Record<string, string> = { mystery: '#f0f3f7', relic: '#ff5ab8', legendary: '#ffb648', epic: '#b48cff', rare: '#38e8ff', uncommon: '#5fe3a1', common: '#9fb3c3' };
const rarityHex = (r: string) => RARITY_HEX[String(r || '').toLowerCase()] || MUTED;
// the Mystery Card (2026-11-04): one special card, four printed, no set number and no image — listed in every break in case one is pulled
// a link we render as an href must be http(s): never a javascript: URL from stored data
const httpOnly = (u: string | null | undefined) => !!u && /^https?:\/\//i.test(u);
const isMystery = (c: { rarity: string }) => String(c.rarity || '').toLowerCase() === 'mystery';

type Bidder = { name: string; username: string | null };
type Lot = {
  id: number;
  card: { id: number; name: string; rarity: string; type: string | null; attack: number | null; defence: number | null; imageUrl: string | null; setNumber: number | null };
  floorInc: string;
  floorUnits: string;
  status: 'open' | 'sold' | 'unsold' | 'cancelled';
  pulledCount: number;
  bidders: number;
  bidCount: number;
  top: { inc: string; units: string; bidder: Bidder; at: string } | null;
  lastBids: { inc: string; status: string; bidder: Bidder; at: string }[];
  winner: { inc: string } | null;
};
type Event = {
  id: number;
  title: string;
  description: string;
  streamUrl: string | null;
  streamLive: boolean;
  status: 'draft' | 'live' | 'closed' | 'opening' | 'settled' | 'cancelled';
  live: boolean;
  startsAt: string;
  endsAt: string;
  latestEndsAt: string | null;
  extensionSeconds: number;
  packs: number;
  cardsPerPack: number;
  minRaiseInc: string;
  minRaisePct: number;
  pulled: number;
};
type Me = {
  standing: Record<number, { inc: string; units: string; status: 'standing' | 'won'; bidId: number }>;
  history: { lotId: number; card: string; inc: string; status: string; mode: string; at: string }[];
  taken: string;
  refunded: string;
  balances: { earned: string; wallet: string; hasCustodyWallet: boolean };
  canBid: boolean;
  shipment: { id: number; status: 'awaiting_address' | 'quoted' | 'paid' | 'shipped'; copies: number; lots: { name: string; copies: number }[]; orderId: number | null; payBy: string | null; paidAt: string | null } | null;
};
/* one parcel of mine (GET /card-auctions/me/shipments): what came out for me from a break, the
 * postage's state, and the tracking once posted */
export type Parcel = {
  id: number;
  auctionId: number;
  auctionTitle: string | null;
  status: 'awaiting_address' | 'quoted' | 'paid' | 'shipped';
  copies: number;
  lots: { name: string; copies: number }[];
  orderId: number | null;
  payBy: string | null;
  paidAt: string | null;
  postage: number | null;
  address: { name: string; line1: string; line2: string | null; city: string; state: string | null; postcode: string; country: string } | null;
  tracking: { carrier: string | null; number: string | null; url: string | null; shippedAt: string | null } | null;
  createdAt: string;
};
type Mode = 'earnedTokens' | 'custody_inc' | 'external_inc';

const num = (s: string | number | null | undefined) => Number(String(s ?? '0').replace(/,/g, ''));
const words = (e: unknown, fallback: string) => {
  const err = e as { data?: { message?: string; code?: string }; message?: string } | null;
  return err?.data?.message || err?.message || fallback;
};
const bidderLabel = (b: Bidder | null | undefined) => (b ? (b.username ? `${b.name} · @${b.username}` : b.name) : '—');
const whenAu = (iso: string) => new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** The least the next bid may be: the floor, or the top + the raise rule. */
const nextMin = (ev: Event, lot: Lot) => {
  if (!lot.top) return num(lot.floorInc);
  const top = num(lot.top.inc);
  return top + Math.max(num(ev.minRaiseInc), (top * ev.minRaisePct) / 100);
};
const fmt = (n: number) => n.toLocaleString('en-AU', { maximumFractionDigits: 2 });

/* ── one way to pay: lit, dimmed with the reason, or busy ── */
const PayRow: React.FC<{ label: string; sub: string; accent: 'gold' | 'cyan'; reason?: string | null; busy?: boolean; disabled?: boolean; onClick: () => void }> = ({ label, sub, accent, reason, busy, disabled, onClick }) => {
  const off = !!disabled || !!reason;
  const color = accent === 'gold' ? GOLD : PT.teal;
  const rgb = accent === 'gold' ? '232,200,119' : '56,232,255';
  return (
    <Box component="button" type="button" onClick={onClick} disabled={off || busy} sx={{
      width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', columnGap: 1.5, rowGap: 0.5, textAlign: 'left',
      p: '11px 14px', borderRadius: '12px', border: '1.5px solid', borderColor: off ? 'rgba(255,255,255,0.12)' : `rgba(${rgb},${busy ? 0.95 : 0.6})`,
      background: off ? 'rgba(255,255,255,0.03)' : `rgba(${rgb},${busy ? 0.18 : 0.09})`, color: off ? MUTED : color, cursor: off ? 'not-allowed' : busy ? 'progress' : 'pointer',
      transition: 'background .15s, border-color .15s', '&:not(:disabled):hover': { background: `rgba(${rgb},0.18)` }, '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 }
    }}>
      <Box sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: 13.5, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.2 }}>{label}</Box>
      <Box sx={{ ...stencil, color: off ? MUTED : color, letterSpacing: '0.14em', display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        {busy ? <CircularProgress size={13} thickness={5} sx={{ color }} /> : null}
        {busy ? 'WORKING…' : reason || 'BID ›'}
      </Box>
      <Box sx={{ ...stencil, gridColumn: '1 / -1', letterSpacing: '0.1em', color: off ? 'rgba(142,163,179,0.75)' : 'rgba(238,246,251,0.72)' }}>{sub}</Box>
    </Box>
  );
};

/* ── HOW IT WORKS ── */
const HowItWorks: React.FC<{ open: boolean; onClose: () => void; ev: Event | null }> = ({ open, onClose, ev }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, display: 'grid', gap: 2 }}>
      <Typography sx={{ ...kickerSx, color: GOLD }}>How it works</Typography>
      <Typography sx={{ color: INK, fontWeight: 800, fontSize: 20 }}>The Incrypt Break</Typography>
      {[
        ['The packs', `The admin opens ${ev ? `${ev.packs} sealed packs of ${ev.cardsPerPack}` : 'sealed packs'} of the physical Angelic set live on stream. Before the stream, you bid INC to secure the cards you want.`],
        ['Bidding', 'Every Angelic card is listed. Place a bid on any card with your earned INC or from your wallet; the INC is taken when you bid. If someone outbids you, your INC comes straight back as earned INC and we email you. Raise as often as you like.'],
        ['The close', 'Bids close at the advertised time, before the stream. The highest bid on each card at the close secures that card: it is yours, and your bid is final from that moment.'],
        ['The draw', 'On stream, every card drawn from the packs goes to the member who secured it — every copy of it, for the one bid. A card that is not drawn in this break has no copies to send; the bid that secured it is not returned.'],
        ['The Mystery Card', 'One special card is listed with no set number: only four exist. It is in every break in case one comes out of the packs — bid on it like any other card.'],
        ['Shipping', 'After the stream, winners enter a delivery address and pay the postage for one parcel holding everything they won. Cards are posted once postage is paid.'],
        ['Who can bid', 'Any member with INC. The usual purchase rules apply. A wallet bid that is outbid comes back as earned INC, not on-chain INC.']
      ].map(([h, t]) => (
        <Box key={h}>
          <Typography sx={{ ...stencil, color: GOLD, mb: 0.5 }}>{h}</Typography>
          <Typography sx={{ color: '#cfd9e2', fontSize: 14, lineHeight: 1.6 }}>{t}</Typography>
        </Box>
      ))}
      <Button onClick={onClose} sx={{ ...ghostSx, justifySelf: 'start' }}>Close</Button>
    </Box>
  </Dialog>
);

/* ── the bid sheet ── */
const BidSheet: React.FC<{ ev: Event; lot: Lot | null; me: Me | null; onClose: () => void; onDone: (lot: Lot | null, ev: Event | null) => void }> = ({ ev, lot, me, onClose, onDone }) => {
  const { post } = useNetwork();
  const { ready, ensureReady, notReadyMessage } = useWalletReady();
  const { publicKey, signTransaction } = useWallet();
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const lotId = lot ? lot.id : null;
  // a new lot opens: start afresh. The same lot refreshing under the sheet (after a bid, a poll) keeps the outcome.
  useEffect(() => { if (lotId != null) { setError(null); setHint(null); setOutcome(null); } }, [lotId]);
  // the field starts at the minimum when a lot opens; a poll that raises the minimum lifts it, but never
  // overwrites what the member typed above it (2026-11-05)
  const minNow = lot ? Math.ceil(nextMin(ev, lot)) : 0;
  useEffect(() => { if (lotId != null && !outcome) setAmount((cur) => { const n = Number(cur); return cur === '' || !Number.isFinite(n) || n < minNow ? String(minNow) : cur; }); }, [lotId, minNow, outcome]);
  if (!lot) return null;
  const min = nextMin(ev, lot);
  const inc = Number(amount);
  const valid = Number.isFinite(inc) && inc >= min && inc <= 1000000;
  const mine = me?.standing?.[lot.id];
  const earned = me ? num(me.balances.earned) : 0;
  const wallet = me ? num(me.balances.wallet) : 0;
  const earnedShort = valid && earned < inc;
  const walletShort = valid && wallet < inc;
  const leading = mine && mine.status === 'standing' && lot.top && num(lot.top.inc) === num(mine.inc);

  const pay = async (mode: Mode) => {
    if (!valid || busy) return;
    setError(null); setHint(null);
    let connectedWallet: string | null = null;
    if (mode !== 'earnedTokens') {
      if (!ensureReady() || !publicKey || !signTransaction) { setHint(notReadyMessage); return; }
      connectedWallet = publicKey.toBase58();
    }
    setBusy(mode);
    try {
      const r = (await post(`/card-auctions/${ev.id}/lots/${lot.id}/bids`, { inc, mode, connectedWallet }, true)) as
        { paid: true; lot: Lot; auction: Event } | { paid: false; paymentId: number; transactionBase64: string };
      if (r.paid) { setOutcome(`You lead ${lot.card.name} at ${fmt(inc)} INC.`); onDone(r.lot, r.auction); return; }
      const tx = Transaction.from(Buffer.from(r.transactionBase64, 'base64'));
      const signed = await signTransaction!(tx);
      const signedTransactionBase64 = Buffer.from(signed.serialize({ requireAllSignatures: false, verifySignatures: false })).toString('base64');
      const c = (await post(`/card-auctions/${ev.id}/lots/${lot.id}/bids/confirm`, { paymentId: r.paymentId, signedTransactionBase64, connectedWallet }, true)) as
        { status: 'standing' | 'refunded'; why: string | null; lot: Lot | null; auction: Event | null };
      if (c.status === 'standing') setOutcome(`You lead ${lot.card.name} at ${fmt(inc)} INC.`);
      else setOutcome(c.why === 'late' ? 'Bidding closed while you were signing — your INC is back as earned INC.' : 'A higher bid landed while you were signing — your INC is back as earned INC. Bid again?');
      onDone(c.lot, c.auction);
    } catch (e) {
      setError(words(e, 'The bid did not go through. Nothing has been taken.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="md" PaperProps={{ sx: { ...dialogPaperSx, border: `1.5px solid ${GOLD_BORDER}` } }}>
      <Box sx={{ p: { xs: 2.5, sm: 3.5 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px minmax(0, 1fr)', md: '300px minmax(0, 1fr)' }, gap: { xs: 2.5, md: 4 }, alignItems: 'start' }}>
        <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${rarityHex(lot.card.rarity)}88`, boxShadow: `0 24px 60px -24px rgba(0,0,0,0.9), 0 0 32px ${rarityHex(lot.card.rarity)}33`, aspectRatio: '5 / 7', background: '#0b1119', maxWidth: { xs: 260, sm: 'none' }, mx: { xs: 'auto', sm: 0 }, width: '100%' }}>
          {lot.card.imageUrl && <Box component="img" src={lot.card.imageUrl} alt={lot.card.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </Box>
        <Box sx={{ display: 'grid', gap: 1.5, alignContent: 'start' }}>
          <Box>
            <Typography sx={{ ...stencil, color: rarityHex(lot.card.rarity) }}>{lot.card.rarity}{lot.card.setNumber != null ? ` · #${lot.card.setNumber}` : ''}{lot.card.attack != null ? ` · ${lot.card.attack} / ${lot.card.defence ?? '–'}` : ''}</Typography>
            <Typography sx={{ color: INK, fontWeight: 800, fontSize: 20, lineHeight: 1.15, mt: 0.5 }}>{lot.card.name}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, py: 1.25, borderTop: `1px solid ${GOLD}33`, borderBottom: `1px solid ${GOLD}33` }}>
            <Box>
              <Typography sx={stencil}>{lot.top ? 'Leading bid' : 'Floor'}</Typography>
              <Typography sx={{ color: GOLD, fontWeight: 800, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>{lot.top ? lot.top.inc : lot.floorInc} <Box component="span" sx={{ fontSize: 12 }}>INC</Box></Typography>
              <Typography sx={{ color: '#cfd9e2', fontSize: 12.5 }}>{lot.top ? bidderLabel(lot.top.bidder) : 'no bids yet'}</Typography>
            </Box>
            <Box>
              <Typography sx={stencil}>You</Typography>
              <Typography sx={{ color: mine ? GOLD : INK, fontWeight: 800, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>{mine ? mine.inc : '—'}{mine ? <Box component="span" sx={{ fontSize: 12 }}> INC</Box> : null}</Typography>
              <Typography sx={{ color: MUTED, fontSize: 12.5 }}>{mine ? (mine.status === 'won' ? 'secured at the close' : 'leading') : 'no bid yet'}</Typography>
            </Box>
          </Box>
          {lot.lastBids.length > 0 && (
            <Box>
              <Typography sx={{ ...stencil, mb: 0.5 }}>Last bids</Typography>
              {lot.lastBids.map((b, i) => (
                <Typography key={i} sx={{ color: i === 0 ? '#cfd9e2' : MUTED, fontSize: 12.5, fontFamily: MONO }}>{b.inc} INC · {bidderLabel(b.bidder)} · {whenAu(b.at)}{b.status === 'refunded' ? ' · outbid' : ''}</Typography>
              ))}
            </Box>
          )}
          {outcome ? (
            <Typography sx={{ color: GREEN, fontWeight: 700, fontSize: 14 }}>{outcome}</Typography>
          ) : !ev.live || lot.status !== 'open' ? (
            <Typography sx={{ color: MUTED, fontSize: 13.5 }}>Bidding on this card is closed.</Typography>
          ) : leading ? (
            <Typography sx={{ color: MUTED, fontSize: 13.5 }}>You hold the top bid. There is nothing to raise until someone beats it — then your INC comes back and you can bid again.</Typography>
          ) : (
            <>
              <TextField label={`Your bid (INC) — at least ${fmt(min)}`} value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ ...(fieldSx as Record<string, unknown>), '& .MuiInputBase-root': { fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums', minHeight: 64 }, '& .MuiInputBase-input': { py: 2 } }} inputMode="decimal" fullWidth
                InputProps={{ endAdornment: <Box component="span" sx={{ ...stencil, color: GOLD, fontSize: 12, letterSpacing: '0.2em', pr: 0.5 }}>INC</Box> }}
                helperText={valid ? `${fmt(inc)} INC is taken when you bid, and comes back as earned INC if you are outbid.` : `The bid must be at least ${fmt(min)} INC.`} />
              <Box sx={{ display: 'grid', gap: 1 }}>
                <PayRow label="Bid with earned INC" sub={`BALANCE · ${me ? me.balances.earned : '—'} INC`} accent="gold" reason={!valid ? 'ENTER A BID' : earnedShort ? 'NOT ENOUGH' : null} busy={busy === 'earnedTokens'} disabled={!!busy} onClick={() => pay('earnedTokens')} />
                <PayRow label="Bid from my INC wallet" sub={me?.balances.hasCustodyWallet ? `BALANCE · ${me.balances.wallet} INC · ON-CHAIN` : 'YOU HAVE NO INC WALLET YET'} accent="cyan" reason={!valid ? 'ENTER A BID' : !me?.balances.hasCustodyWallet ? 'NO WALLET' : walletShort ? 'NOT ENOUGH' : null} busy={busy === 'custody_inc'} disabled={!!busy} onClick={() => pay('custody_inc')} />
                <PayRow label="Bid from an external wallet" sub={ready ? 'ON-CHAIN · YOUR CONNECTED WALLET PAYS THE NETWORK FEE' : 'ON-CHAIN · CONNECT A WALLET FIRST, THEN TAP AGAIN'} accent="cyan" reason={!valid ? 'ENTER A BID' : null} busy={busy === 'external_inc'} disabled={!!busy} onClick={() => pay('external_inc')} />
              </Box>
              <Typography sx={{ color: MUTED, fontSize: 12, lineHeight: 1.5 }}>
                The highest bid at the close secures the card and is final, whether or not the card is drawn from the packs. A wallet bid that is outbid comes back as earned INC. A wallet bid can be beaten while you are signing; if it is, it comes straight back.
              </Typography>
            </>
          )}
          {hint && <Typography sx={{ color: GOLD, fontSize: 13 }}>{hint}</Typography>}
          {error && <Typography sx={{ color: RED, fontSize: 13 }}>{error}</Typography>}
          <Button onClick={onClose} disabled={!!busy} sx={{ ...ghostSx, justifySelf: 'start' }}>{outcome ? 'Done' : 'Not now'}</Button>
        </Box>
      </Box>
    </Dialog>
  );
};

/* ── the postage step, after the opening ── */
const blankShipping = (u: { firstName?: string; lastName?: string; email?: string; phone?: string } | null): Shipping => ({
  name: [u?.firstName, u?.lastName].filter(Boolean).join(' '), email: u?.email || '', phone: u?.phone || '', line1: '', line2: '', city: '', state: '', postcode: '', country: 'AU'
});
const PostageStep: React.FC<{ auctionId: number; shipment: Parcel; onPaid: () => void }> = ({ auctionId, shipment: s, onPaid }) => {
  const { post } = useNetwork();
  const { user } = useUser();
  const [shipping, setShipping] = useState<Shipping>(() => blankShipping(user as never));
  const [quote, setQuote] = useState<{ cost: number; service: string | null } | null>(null);
  const [payment, setPayment] = useState<{ clientSecret?: string; amount: number; publishableKey?: string; free?: boolean } | null>(null);
  // one attempt id per mounted step: a retry (Back, then Pay again; a second tab) reuses the same
  // order and PaymentIntent instead of minting another (2026-11-05)
  const attemptRef = useRef(`${s.id}-${Date.now()}`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(() => State.getStatesOfCountry(shipping.country) || [], [shipping.country]);
  const activeStripe = useMemo(() => (payment?.publishableKey ? loadStripe(payment.publishableKey) : stripePromise), [payment?.publishableKey]);
  const set = (k: keyof Shipping) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setShipping((s) => ({ ...s, [k]: e.target.value }));
  if (s.status === 'shipped') {
    return (
      <Box sx={{ ...panelPlain, p: 2.5, border: `1px solid ${GREEN}66`, display: 'grid', gap: 0.5 }}>
        <Typography sx={{ ...kickerSx, color: GREEN }}>Posted{s.auctionTitle ? ` · ${s.auctionTitle}` : ''}</Typography>
        <Typography sx={{ color: INK, fontSize: 14, mt: 0.5 }}>{s.copies} card{s.copies === 1 ? '' : 's'} on the way: {s.lots.map((l) => `${l.name} × ${l.copies}`).join(', ')}.</Typography>
        {s.tracking && (s.tracking.number || s.tracking.carrier) && (
          <Typography sx={{ color: INK, fontSize: 14 }}>
            {s.tracking.carrier || 'Tracking'}{s.tracking.number ? <> · {httpOnly(s.tracking.url) ? <a href={s.tracking.url || undefined} target="_blank" rel="noreferrer" style={{ color: PT.teal, fontFamily: MONO }}>{s.tracking.number}</a> : <span style={{ fontFamily: MONO }}>{s.tracking.number}</span>}</> : null}
            {s.tracking.shippedAt ? <span style={{ color: MUTED }}> · posted {whenAu(s.tracking.shippedAt)}</span> : null}
          </Typography>
        )}
        {s.address && <Typography sx={{ color: MUTED, fontSize: 12.5 }}>to {[s.address.line1, s.address.city, s.address.postcode].filter(Boolean).join(', ')}</Typography>}
      </Box>
    );
  }
  if (s.status === 'paid') {
    return (
      <Box sx={{ ...panelPlain, p: 2.5, border: `1px solid ${GREEN}66`, display: 'grid', gap: 0.5 }}>
        <Typography sx={{ ...kickerSx, color: GREEN }}>Postage paid{s.auctionTitle ? ` · ${s.auctionTitle}` : ''}</Typography>
        <Typography sx={{ color: INK, fontSize: 14, mt: 0.5 }}>{s.copies} card{s.copies === 1 ? '' : 's'} being packed: {s.lots.map((l) => `${l.name} × ${l.copies}`).join(', ')}. We will email you the tracking once it is posted.</Typography>
        {s.address && <Typography sx={{ color: MUTED, fontSize: 12.5 }}>to {[s.address.line1, s.address.city, s.address.postcode].filter(Boolean).join(', ')}</Typography>}
      </Box>
    );
  }
  const getQuote = async () => {
    setErr(null); setBusy(true);
    try {
      const q = (await post(`/card-auctions/${auctionId}/shipment/quote`, { shipping }, true)) as { cost: number; service: string | null };
      setQuote(q);
    } catch (e) { setErr(words(e, 'Could not quote the postage.')); } finally { setBusy(false); }
  };
  const start = async () => {
    const missing = (['name', 'email', 'line1', 'city', 'postcode', 'country'] as const).filter((k) => !shipping[k].trim());
    if (missing.length) { setErr('Please fill in your name, email, address line 1, city, postcode and country.'); return; }
    setErr(null); setBusy(true);
    try {
      const r = (await post(`/card-auctions/${auctionId}/shipment/payment-intent`, { shipping, attemptId: attemptRef.current }, true)) as { clientSecret?: string; amount: number; publishableKey?: string; free?: boolean };
      if (r.free) { onPaid(); return; }
      setPayment(r);
    } catch (e) { setErr(words(e, 'Could not start the payment.')); } finally { setBusy(false); }
  };
  return (
    <Box sx={{ ...panelPlain, p: 2.5, border: `1px solid ${GOLD_BORDER}`, background: `radial-gradient(120% 80% at 50% -10%, ${GOLD_SOFT}, transparent 55%), ${PT.surface}`, display: 'grid', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShippingOutlinedIcon sx={{ color: GOLD }} />
        <Typography sx={{ ...kickerSx, color: GOLD }}>Your cards are ready to post{s.auctionTitle ? ` · ${s.auctionTitle}` : ''}</Typography>
      </Box>
      <Typography sx={{ color: INK, fontSize: 14 }}>{s.copies} card{s.copies === 1 ? '' : 's'} came out for you: {s.lots.map((l) => `${l.name} × ${l.copies}`).join(', ')}. Enter your address and pay the postage for one parcel.{s.payBy ? ` Please pay by ${whenAu(s.payBy)}.` : ''}</Typography>
      {payment && payment.clientSecret ? (
        <Elements stripe={activeStripe} options={{ clientSecret: payment.clientSecret, appearance: { theme: 'night' } }}>
          <PaymentForm amount={payment.amount} billing={{ name: shipping.name, email: shipping.email, phone: shipping.phone || undefined, address: { line1: shipping.line1, line2: shipping.line2 || undefined, city: shipping.city, state: shipping.state || undefined, postal_code: shipping.postcode, country: shipping.country } }} onDone={onPaid} onBack={() => setPayment(null)} />
        </Elements>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField label="Full name" value={shipping.name} onChange={set('name')} size="small" sx={fieldSx} />
            <TextField label="Email" value={shipping.email} onChange={set('email')} size="small" sx={fieldSx} />
            <TextField label="Phone" value={shipping.phone} onChange={set('phone')} size="small" sx={fieldSx} />
            <TextField select label="Country" value={shipping.country} onChange={(e) => setShipping((v) => ({ ...v, country: e.target.value, state: '' }))} size="small" sx={[fieldSx, selectSx] as SxProps<Theme>} SelectProps={{ MenuProps: selectMenuProps }}>
              {countries.map((c) => <MenuItem key={c.isoCode} value={c.isoCode}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label="Address line 1" value={shipping.line1} onChange={set('line1')} size="small" sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} />
            <TextField label="Address line 2" value={shipping.line2} onChange={set('line2')} size="small" sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} />
            <TextField label="City / suburb" value={shipping.city} onChange={set('city')} size="small" sx={fieldSx} />
            {states.length ? (
              <TextField select label="State" value={shipping.state} onChange={set('state')} size="small" sx={[fieldSx, selectSx] as SxProps<Theme>} SelectProps={{ MenuProps: selectMenuProps }}>
                {states.map((st) => <MenuItem key={st.isoCode} value={st.isoCode}>{st.name}</MenuItem>)}
              </TextField>
            ) : <TextField label="State / region" value={shipping.state} onChange={set('state')} size="small" sx={fieldSx} />}
            <TextField label="Postcode" value={shipping.postcode} onChange={set('postcode')} size="small" sx={fieldSx} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button onClick={getQuote} disabled={busy || !shipping.postcode || !shipping.country} sx={ghostBtnSx}>Quote the postage</Button>
            {quote && <Typography sx={{ color: INK, fontSize: 14 }}>Postage: <b>A${Number(quote.cost ?? 0).toFixed(2)}</b>{quote.service ? ` · ${quote.service}` : ''}</Typography>}
            <Button onClick={start} disabled={busy} sx={goldBtn}>{busy ? 'One moment…' : 'Pay the postage'}</Button>
          </Box>
          {err && <Typography sx={{ color: RED, fontSize: 13 }}>{err}</Typography>}
        </>
      )}
    </Box>
  );
};

/* ── YOUR PARCELS — every parcel of mine, whatever break is on. An unpaid postage must stay
 * reachable after the next break starts and after the settled one leaves the page, so this
 * reads /card-auctions/me/shipments on its own, not the current event. Unpaid first. ── */
export const YourParcels: React.FC<{ title?: boolean }> = ({ title = true }) => {
  const { get } = useNetwork();
  const [parcels, setParcels] = useState<Parcel[] | null>(null);
  const getRef = useRef(get);
  getRef.current = get;
  const load = useCallback(async () => {
    try {
      const r = (await getRef.current('/card-auctions/me/shipments', true)) as { shipments: Parcel[] };
      setParcels(r.shipments || []);
    } catch { setParcels((p) => p || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!parcels || !parcels.length) return null;
  const rank = (p: Parcel) => (p.status === 'paid' ? 1 : p.status === 'shipped' ? 2 : 0);
  const sorted = [...parcels].sort((a, b) => rank(a) - rank(b) || b.id - a.id);
  const unpaid = parcels.filter((p) => rank(p) === 0).length;
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ ...stencil, color: GOLD }}>Your parcels</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12.5 }}>{unpaid ? `${unpaid} waiting for an address and postage` : 'all paid'}</Typography>
        </Box>
      )}
      {sorted.map((p) => <PostageStep key={p.id} auctionId={p.auctionId} shipment={p} onPaid={load} />)}
    </Box>
  );
};

/* ── the page ── */
const CardAuction: React.FC = () => {
  const { get } = useNetwork();
  const [ev, setEv] = useState<Event | null>(null);
  const [lots, setLots] = useState<Lot[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState<Lot | null>(null);
  const [how, setHow] = useState(false);
  const [filter, setFilter] = useState<'all' | 'mine' | string>('all');
  const [query, setQuery] = useState('');
  const getRef = useRef(get);
  getRef.current = get;

  const load = useCallback(async () => {
    try {
      const v = (await getRef.current('/card-auctions/current', false)) as { auction: Event | null; lots: Lot[] };
      setEv(v.auction);
      setLots(v.lots || []);
      if (v.auction) {
        const m = (await getRef.current(`/card-auctions/${v.auction.id}/me`, true)) as { you: Me };
        setMe(m.you);
      }
    } catch { /* keep what we have */ } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!ev || (!['live', 'closed', 'opening'].includes(ev.status) && !ev.streamLive)) return undefined;
    const t = setInterval(load, ev.status === 'opening' ? 5000 : 15000);
    return () => clearInterval(t);
  }, [ev, load]);

  const rarities = useMemo(() => Array.from(new Set(lots.map((l) => String(l.card.rarity).toLowerCase()))), [lots]);
  // the search: every word typed must appear in the card's name, rarity or set number
  const words = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);
  const shown = useMemo(() => lots.filter((l) => {
    if (filter === 'mine' ? !me?.standing?.[l.id] : filter !== 'all' && String(l.card.rarity).toLowerCase() !== filter) return false;
    if (!words.length) return true;
    const hay = `${l.card.name} ${l.card.rarity} #${l.card.setNumber ?? ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  }), [lots, filter, me, words]);
  const myLead = me ? Object.values(me.standing).length : 0;

  if (!loaded) return <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={24} sx={{ color: GOLD }} /></Box>;
  if (!ev) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, color: PT.text }}>
        <Typography sx={{ ...kickerSx, color: GOLD }}>Incrypt Break</Typography>
        <Typography sx={{ color: INK, fontWeight: 800, fontSize: 24, mt: 1 }}>No break is on right now</Typography>
        <Typography sx={{ color: MUTED, fontSize: 14, mt: 1 }}>When the next live pack opening is announced, every card of the Angelic set will be listed here to bid on.</Typography>
        <Box sx={{ mt: 3 }}><YourParcels /></Box>
      </Box>
    );
  }
  const cap = ev.packs * ev.cardsPerPack;
  // three of the set's relics (any three cards with art when there are fewer), for the masthead's fan
  const fan = [...lots.filter((l) => String(l.card.rarity).toLowerCase() === 'relic' && l.card.imageUrl), ...lots.filter((l) => l.card.imageUrl)].filter((l, i, arr) => arr.findIndex((x) => x.id === l.id) === i).slice(0, 3);
  const names = new Set(lots.map((l) => l.card.name)).size;
  const stateLine = ev.status === 'live' && ev.live ? 'bidding open' : ev.status === 'live' ? `bidding opens ${whenAu(ev.startsAt)}` : ev.status === 'closed' ? 'bidding closed · the opening is next' : ev.status === 'opening' ? `opening the packs · ${ev.pulled} of ${cap} drawn` : ev.status === 'settled' ? 'the packs are open' : 'cancelled';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, color: PT.text, display: 'grid', gap: 3 }}>
      {/* THE MASTHEAD — the set auction's panel language (the hatched gold ground, the ruled
          particulars, Orbitron for the title): a fan of the set's relics, the title and what
          matters about this break, and the countdown as a ticket at the right. */}
      <Box sx={{ position: 'relative', borderRadius: '6px', border: `1px solid ${GOLD}66`, outline: `1px solid ${GOLD}2e`, outlineOffset: 5, overflow: 'hidden', background: `repeating-linear-gradient(135deg, ${GOLD}08 0 2px, transparent 2px 14px), linear-gradient(180deg, #0d141d, #070b11)`, boxShadow: `0 40px 90px -50px ${GOLD}88, inset 0 1px 0 rgba(255,255,255,0.04)`, display: 'grid', gridTemplateColumns: { xs: '1fr', md: fan.length ? 'auto minmax(0,1fr)' : 'minmax(0,1fr)', lg: fan.length ? 'auto minmax(0,1fr) auto' : 'minmax(0,1fr) auto' }, gap: { xs: 2.5, md: 3, lg: 4 }, alignItems: 'center', p: { xs: 2.5, md: 3.5 }, '@keyframes incBreakPulse': { '0%, 100%': { boxShadow: `0 0 0 0 ${GOLD}66` }, '50%': { boxShadow: `0 0 0 6px ${GOLD}00` } } }}>
        {fan.length > 0 && (
          <Box sx={{ position: 'relative', width: { xs: 272, md: 304 }, height: { xs: 172, md: 196 }, flexShrink: 0, mr: { md: 1 } }}>
            {fan.map((l, i) => (
              <Box key={l.id} component="img" src={l.card.imageUrl || ''} alt="" loading="lazy" sx={{ position: 'absolute', left: `${i * 28}%`, bottom: i === 1 ? 10 : 0, width: { xs: 104, md: 118 }, aspectRatio: '5 / 7', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${rarityHex(l.card.rarity)}aa`, boxShadow: `0 18px 40px -16px rgba(0,0,0,0.9), 0 0 24px ${rarityHex(l.card.rarity)}33`, transform: `rotate(${(i - 1) * 9}deg)`, transformOrigin: '50% 100%', zIndex: i === 1 ? 3 : 2 - Math.abs(i - 1) }} />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'grid', gap: 1.25, minWidth: 0 }}>
          <Typography sx={{ ...stencil, letterSpacing: '0.28em', color: GOLD, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {ev.streamLive && (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '999px', background: '#ff2d4a', color: '#fff', font: mono, letterSpacing: '0.18em', '@keyframes incLivePulse': { '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,45,74,0.6)' }, '70%': { boxShadow: '0 0 0 8px rgba(255,45,74,0)' } }, animation: 'incLivePulse 1.6s ease-out infinite' }}>
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
                LIVE NOW
              </Box>
            )}
            {ev.live && !ev.streamLive && <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', background: GOLD, animation: 'incBreakPulse 1.6s ease-out infinite' }} />}
            Angelic set · first print · {stateLine}
          </Typography>
          <Typography className="inc-orbitron" sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: INK, lineHeight: 1.12, textWrap: 'balance' }}>{ev.title}</Typography>
          <Typography sx={{ color: MUTED, fontSize: 14, lineHeight: 1.6, maxWidth: 560 }}>{ev.description || `${ev.packs} packs of ${ev.cardsPerPack} cards opened live. Bid on the cards you want; the highest bid at the close secures the card.`}</Typography>

          {/* the particulars, ruled */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 1.5, sm: 2.5 }, py: 1.5, mt: 0.5, borderTop: `1px solid ${GOLD}33`, borderBottom: `1px solid ${GOLD}33`, minWidth: 0 }}>
            <Box>
              <Typography sx={stencil}>The packs</Typography>
              <Typography sx={{ mt: 0.5, color: INK, fontWeight: 800, fontSize: 18, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{ev.packs} × {ev.cardsPerPack}</Typography>
              <Typography sx={{ mt: 0.5, color: MUTED, fontSize: 12.5 }}>{cap} cards drawn on stream</Typography>
            </Box>
            <Box sx={{ borderLeft: { sm: `1px solid ${GOLD}33` }, pl: { sm: 2.5 }, minWidth: 0 }}>
              <Typography sx={stencil}>The set</Typography>
              <Typography sx={{ mt: 0.5, color: INK, fontWeight: 800, fontSize: 18, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{lots.length} cards</Typography>
              <Typography sx={{ mt: 0.5, color: MUTED, fontSize: 12.5 }}>{names} names × {rarities.length} rarities</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, borderLeft: { sm: `1px solid ${GOLD}33` }, pl: { sm: 2.5 }, minWidth: 0 }}>
              <Typography sx={stencil}>You</Typography>
              <Typography sx={{ mt: 0.5, color: myLead ? GOLD : INK, fontWeight: 800, fontSize: 18, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{me ? `${myLead} card${myLead === 1 ? '' : 's'}` : '—'}</Typography>
              <Typography sx={{ mt: 0.5, color: MUTED, fontSize: 12.5 }}>{me ? `${me.taken} INC bid · ${me.refunded} INC returned` : 'sign in to bid'}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => setHow(true)} sx={ghostSx}>How it works</Button>
            {httpOnly(ev.streamUrl) && (
              <Button component="a" href={ev.streamUrl || undefined} target="_blank" rel="noreferrer" sx={ev.streamLive ? { ...primaryPlain, background: '#ff2d4a', color: '#fff', boxShadow: '0 10px 30px -12px #ff2d4a', '&:hover': { background: '#ff5a70' } } : ghostSx}>
                {ev.streamLive ? '● Watch live now' : 'Watch the stream'}
              </Button>
            )}
          </Box>
        </Box>

        {/* the ticket */}
        <Box sx={{ position: 'relative', alignSelf: 'stretch', display: 'grid', alignContent: 'center', gap: 1, gridColumn: { xs: 'auto', md: '1 / -1', lg: 'auto' }, pl: { lg: 3.5 }, pt: { xs: 2, lg: 0 }, borderLeft: { lg: `1px dashed ${GOLD}55` }, borderTop: { xs: `1px dashed ${GOLD}55`, lg: 'none' }, minWidth: { lg: 230 } }}>
          <Typography sx={{ ...stencil, color: GOLD }}>{ev.live ? 'Bids close in' : ev.status === 'live' ? 'Bidding opens' : 'Bids closed'}</Typography>
          {ev.live ? (
            <AuctionCountdown endsAt={ev.endsAt} compact tone={{ ink: INK, label: MUTED, box: 'rgba(255,255,255,0.04)', border: `${GOLD}33` }} />
          ) : (
            <Typography sx={{ color: INK, fontSize: 16, fontWeight: 700 }}>{whenAu(ev.status === 'live' ? ev.startsAt : ev.endsAt)}</Typography>
          )}
          <Typography sx={{ color: MUTED, fontSize: 12, lineHeight: 1.5 }}>
            {ev.live ? <>{whenAu(ev.endsAt)}<br />A bid in the last {Math.round(ev.extensionSeconds / 60)} min extends it{ev.latestEndsAt ? `, never past ${whenAu(ev.latestEndsAt)}` : ''}.</> : ev.status === 'opening' ? `${ev.pulled} of ${cap} cards drawn so far` : ev.status === 'settled' ? 'Every pack is open.' : 'The leading bid on each card is final.'}
          </Typography>
        </Box>
      </Box>

      <YourParcels />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          placeholder="Search a card…"
          inputProps={{ 'aria-label': 'Search the cards' }}
          sx={{ ...(fieldSx as Record<string, unknown>), width: { xs: '100%', sm: 260 }, mr: { sm: 1 }, '& .MuiInputBase-root': { fontFamily: MONO, fontSize: 13, letterSpacing: '0.04em', background: 'rgba(255,255,255,0.04)' } }}
          InputProps={{ endAdornment: query ? <Button onClick={() => setQuery('')} size="small" sx={{ minWidth: 0, color: MUTED, font: mono, letterSpacing: '0.12em' }}>CLEAR</Button> : null }}
        />
        {(['all', 'mine', ...rarities] as string[]).map((f) => (
          <Button key={f} size="small" onClick={() => setFilter(f)} sx={{ ...ghostSx, py: 0.5, px: 1.5, font: mono, letterSpacing: '0.14em', color: filter === f ? '#0b0f14' : PT.text, background: filter === f ? (f === 'all' || f === 'mine' ? GOLD : rarityHex(f)) : 'rgba(255,255,255,0.04)', borderColor: filter === f ? 'transparent' : PT.border } as SxProps<Theme>}>
            {f === 'all' ? `ALL · ${lots.length}` : f === 'mine' ? `MINE · ${myLead}` : f.toUpperCase()}
          </Button>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
        {shown.map((l) => {
          const mine = me?.standing[l.id];
          const won = l.status === 'sold' && mine && mine.status === 'won';
          const accent = won ? GOLD : mine ? GOLD : rarityHex(l.card.rarity);
          return (
            <Box key={l.id} component="button" type="button" onClick={() => setOpen(l)} sx={{ textAlign: 'left', cursor: 'pointer', p: 0, ...panelPlain, border: `1px solid ${mine ? GOLD_BORDER : PT.border}`, background: mine ? `radial-gradient(120% 80% at 50% -10%, ${GOLD_SOFT}, transparent 55%), ${PT.surface}` : PT.surface, overflow: 'hidden', display: 'grid', color: PT.text, transition: 'transform .15s, border-color .15s', '&:hover': { transform: 'translateY(-2px)', borderColor: accent } } as SxProps<Theme>}>
              <Box sx={{ position: 'relative', aspectRatio: '5 / 7', background: '#0b1119' }}>
                {l.card.imageUrl ? <Box component="img" src={l.card.imageUrl} alt={l.card.name} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: isMystery(l.card) ? INK : MUTED, fontSize: isMystery(l.card) ? 64 : 12, fontWeight: 800 }}>{isMystery(l.card) ? '?' : 'no image yet'}</Box>}
                <Box sx={{ position: 'absolute', top: 8, left: 8, ...stencil, color: '#0b0f14', background: rarityHex(l.card.rarity), px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 9 }}>{l.card.rarity}</Box>
                {(ev.status === 'opening' || ev.status === 'settled') && l.pulledCount > 0 && <Box sx={{ position: 'absolute', top: 8, right: 8, ...stencil, color: '#0b0f14', background: GREEN, px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 9 }}>pulled × {l.pulledCount}</Box>}
                {mine && <Box sx={{ position: 'absolute', bottom: 8, left: 8, ...stencil, color: '#0b0f14', background: GOLD, px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 9 }}>{mine.status === 'won' ? 'secured' : 'you lead'}</Box>}
              </Box>
              <Box sx={{ p: 1.25, display: 'grid', gap: 0.5 }}>
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: 13.5, lineHeight: 1.2 }}>{l.card.name}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11, fontFamily: MONO }}>{l.card.attack != null ? `${l.card.attack} / ${l.card.defence ?? '–'}` : l.card.type || ''}</Typography>
                <Typography sx={{ color: l.top ? GOLD : MUTED, fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{l.top ? `${l.top.inc} INC` : `from ${l.floorInc} INC`}</Typography>
                <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{l.status === 'sold' ? `secured · ${bidderLabel(l.top?.bidder)}` : l.status === 'unsold' ? 'no bids' : l.top ? `${bidderLabel(l.top.bidder)} · ${l.bidders} bidder${l.bidders === 1 ? '' : 's'}` : 'no bids yet'}</Typography>
              </Box>
            </Box>
          );
        })}
        {shown.length === 0 && <Typography sx={{ color: MUTED, gridColumn: '1 / -1' }}>{words.length ? `No card matches “${query.trim()}”${filter !== 'all' ? ' in this filter' : ''}.` : filter === 'mine' ? 'You are not leading any card.' : 'No cards here.'}</Typography>}
      </Box>

      <BidSheet ev={ev} lot={open ? lots.find((x) => x.id === open.id) || open : null} me={me} onClose={() => setOpen(null)} onDone={(lot, a) => { if (lot) setLots((ls) => ls.map((x) => (x.id === lot.id ? lot : x))); if (a) setEv((e) => (e ? { ...e, ...a } : e)); load(); }} />
      <HowItWorks open={how} onClose={() => setHow(false)} ev={ev} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: MUTED, fontSize: 12 }}><GavelIcon sx={{ fontSize: 16 }} /> Any member with INC may bid. A secured card's bid is final whether or not the card is drawn.</Box>
    </Box>
  );
};

export default CardAuction;
